import type { PageBreadcrumbLink } from "@sg-protect/components";
import type { ODataSelection } from "@sg-protect/odata";
import { type Location, generatePath } from "react-router";
import type { SelectionLocationState, SelectionViewState } from "../components/entity-selection/SelectionPage.tsx";
import { usePaths } from "../paths.ts";

export function getCurrentUrl(location: Pick<Location, "pathname" | "search">): string {
    return `${location.pathname}${location.search}`;
}

/**
 * Breadcrumb-origin tracking for pages reachable from multiple entry points.
 *
 * A page like automation create/update can be opened from Overview, Tenant info,
 * an Insight detail, or directly. To render the correct breadcrumb trail and
 * back-navigation target, the originating page encodes its identity in the URL
 * via `?root=…` (and `rootInsightId=…` for the insights case).
 *
 * Typical flow:
 *   1. Originating page builds a link using `buildRootQuery(origin)`.
 *   2. Destination page calls `getRootOrigin(searchParams)` to recover the origin.
 *   3. Destination page passes that origin to `useOriginCrumbs` (for the top-level
 *      crumb + return target) and optionally `buildInsightDetailCrumb` (for an
 *      intermediate crumb back to an insight detail page).
 */
export type RootOrigin =
    | { kind: "overview" }
    | { kind: "tenantInfo" }
    | { kind: "insights"; insightId: string | null }
    | { kind: "report"; title: string; url: string }
    | { kind: "review"; title: string; url: string }
    | { kind: "activityLog" }
    | { kind: "primary" };

export function getRootOrigin(searchParams: URLSearchParams): RootOrigin {
    const root = searchParams.get("root");

    if (root === "overview") {
        return { kind: "overview" };
    }

    if (root === "tenantInfo") {
        return { kind: "tenantInfo" };
    }

    if (root === "insights") {
        return { kind: "insights", insightId: searchParams.get("rootInsightId") };
    }

    if (root === "report") {
        const url = sanitizeInternalUrl(searchParams.get("reportUrl"));

        if (url) {
            return { kind: "report", title: searchParams.get("reportTitle") ?? "Report", url };
        }
    }

    if (root === "review") {
        const url = sanitizeInternalUrl(searchParams.get("reviewUrl"));

        if (url) {
            return { kind: "review", title: searchParams.get("reviewTitle") ?? "Review details", url };
        }
    }

    if (root === "activityLog") {
        return { kind: "activityLog" };
    }

    return { kind: "primary" };
}

// `reportUrl` is rendered as an anchor href, so only allow same-origin relative paths.
function sanitizeInternalUrl(url: string | null): string | undefined {
    return url && url.startsWith("/") && !url.startsWith("//") ? url : undefined;
}

export function buildRootQuery(origin: RootOrigin): Record<string, string> {
    switch (origin.kind) {
        case "primary":
            return {};
        case "overview":
            return { root: "overview" };
        case "tenantInfo":
            return { root: "tenantInfo" };
        case "insights":
            return origin.insightId ? { root: "insights", rootInsightId: origin.insightId } : { root: "insights" };
        case "report":
            return { root: "report", reportTitle: origin.title, reportUrl: origin.url };
        case "review":
            return { root: "review", reviewTitle: origin.title, reviewUrl: origin.url };
        case "activityLog":
            return { root: "activityLog" };
    }
}

export interface OriginCrumb {
    name: string;
    link: string;
}

export function useOriginCrumbs(origin: RootOrigin, fallback: OriginCrumb): OriginCrumb[] {
    const paths = usePaths();

    switch (origin.kind) {
        case "overview":
            return [{ name: "Home", link: paths.home }];
        case "tenantInfo":
            return [{ name: "Tenant info", link: paths.tenantInfo }];
        case "insights":
            return [{ name: "Insights", link: paths.insights }];
        case "report":
            return [
                { name: "Reports", link: paths.reports },
                { name: origin.title, link: origin.url }
            ];
        case "review":
            return [
                { name: "Reviews", link: paths.reviews },
                { name: origin.title, link: origin.url }
            ];
        case "activityLog":
            return [{ name: "Activity log", link: paths.activityLog }];
        case "primary":
            return [fallback];
    }
}

export interface PmrReturn {
    url: string;
    selection: ODataSelection;
    scopeId?: string;
    view?: SelectionViewState;
}

export function applyPmrReturnState(
    crumbs: OriginCrumb[],
    origin: RootOrigin,
    pmrReturn: PmrReturn | undefined
): PageBreadcrumbLink[] {
    if (origin.kind !== "report" || pmrReturn?.url !== origin.url) {
        return crumbs;
    }

    return crumbs.map(crumb =>
        crumb.link === origin.url
            ? {
                  ...crumb,
                  routerOptions: {
                      state: {
                          selection: pmrReturn.selection,
                          scopeId: pmrReturn.scopeId,
                          view: pmrReturn.view
                      } satisfies SelectionLocationState
                  }
              }
            : crumb
    );
}

interface InsightDetailLink {
    name: string;
    path: string;
}

export function buildInsightDetailCrumb(origin: RootOrigin, insightId: string, detail: InsightDetailLink): OriginCrumb {
    const query = new URLSearchParams(buildRootQuery(origin)).toString();
    const link = `${generatePath(detail.path, { insightId })}${query ? `?${query}` : ""}`;

    return { name: detail.name, link };
}
